import React from 'react';
import { Navigate, Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import PublicNavbar from '../components/public/PublicNavbar';
import LoginHero from '../components/public/LoginHero';
import AuthCard from '../components/public/AuthCard';
import HowItWorks from '../components/public/HowItWorks';
import FeatureCallouts from '../components/public/FeatureCallouts';
import ProductPreview from '../components/public/ProductPreview';
import PricingCards from '../components/public/PricingCards';
import FinalCta from '../components/public/FinalCta';
import { AudienceSection, SpotlightSection, LearnTeaser, FaqSection, PublicFooter, TrustStrip, UpdatesSection } from '../components/public/landingSections';
import { focusRing } from '../components/public/landingStyles';
import type { PublicPlan } from '../services/billing';
import authService from '../services/auth';

const Login: React.FC = () => {
  const [plans, setPlans] = React.useState<PublicPlan[] | null>(null);

  React.useEffect(() => {
    if (process.env.NODE_ENV === 'test') return undefined;
    let cancelled = false;
    import('../services/billing')
      .then(({ default: billingService }) => billingService.catalog())
      .then((rows) => {
        if (!cancelled) setPlans(rows);
      })
      .catch(() => {
        if (!cancelled) setPlans([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (authService.getToken()) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Box
      sx={{
        overflowX: 'hidden',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <PublicNavbar />
      <Box
        id="main"
        component="main"
        tabIndex={-1}
        sx={{ px: { xs: 2, sm: 3, md: 5 }, py: { xs: 3, md: 5 }, maxWidth: 1200, mx: 'auto', minWidth: 0 }}
      >
        <Box
          component="section"
          aria-label="Introduction"
          sx={{
            position: 'relative',
            overflow: 'hidden',
            isolation: 'isolate',
            borderRadius: { xs: 2, md: 3 },
            border: '1px solid',
            borderColor: 'divider',
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 3, md: 5 },
            mb: { xs: 4, md: 6 },
            boxShadow: (theme) =>
              `inset 4px 0 0 ${theme.palette.brand.yellow}, 0 18px 40px color-mix(in srgb, ${theme.palette.brand.navy} 18%, transparent)`,
            backgroundColor: 'background.default',
            backgroundImage: (theme) => {
              const accent = theme.palette.brand.yellow;
              const navy = theme.palette.brand.navy;
              const page = theme.palette.background.default;
              if (theme.palette.mode === 'dark') {
                return `radial-gradient(460px 280px at 100% -8%, color-mix(in srgb, ${accent} 48%, transparent), transparent 70%), radial-gradient(380px 240px at 0% 115%, color-mix(in srgb, ${accent} 26%, transparent), transparent 68%), linear-gradient(165deg, color-mix(in srgb, ${navy} 78%, ${accent}) 0%, ${page} 74%)`;
              }
              return `radial-gradient(520px 300px at 100% -10%, color-mix(in srgb, ${accent} 62%, transparent), transparent 68%), radial-gradient(360px 240px at 0% 110%, color-mix(in srgb, ${navy} 14%, transparent), transparent 70%), linear-gradient(165deg, color-mix(in srgb, ${accent} 16%, #ffffff) 0%, ${page} 72%)`;
            },
          }}
        >
          <Box
            className="ds-aurora ds-aurora-a"
            aria-hidden="true"
            sx={{
              top: { xs: -140, md: -90 },
              right: { xs: -160, md: -50 },
              bgcolor: 'brand.yellow',
              opacity: { xs: 0.35, md: 0.55 },
            }}
          />
          <Box
            className="ds-aurora ds-aurora-b"
            aria-hidden="true"
            sx={{
              bottom: -120,
              left: { xs: -80, md: '18%' },
              bgcolor: 'brand.navy',
              opacity: 0.3,
            }}
          />
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 0.92fr) minmax(300px, 1.08fr)' },
              gap: { xs: 3, md: 3 },
              alignItems: 'stretch',
            }}
          >
            <LoginHero />
            <Box sx={{ minWidth: 0, display: 'flex' }}>
              <AuthCard />
            </Box>
          </Box>
        </Box>
        <ProductPreview />
        <Box className="ds-rise" sx={{ display: 'grid', gap: { xs: 6, md: 9 } }}>
          <HowItWorks />
          <TrustStrip />
          <FeatureCallouts />
          <AudienceSection />
          <SpotlightSection />
          <LearnTeaser />
          {plans && plans.length > 0 ? (
            <PricingCards plans={plans} />
          ) : (
            <Box component="section" id="pricing" aria-labelledby="pricing-heading" aria-busy={plans === null}>
              <Typography id="pricing-heading" variant="h2" sx={{ fontWeight: 800, fontSize: { xs: 32, md: 44 }, mb: 1 }}>
                Simple pricing
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
                {plans === null
                  ? 'Loading the plan catalog…'
                  : 'The plan catalog did not load, so no prices are shown. The Free plan does not require a credit card. Paid allowances are the ones published in the catalog, not a promise about profit.'}
              </Typography>
              <Button component={RouterLink} to="/billing-policy" variant="outlined" sx={focusRing}>
                Billing policy
              </Button>
            </Box>
          )}
          <FaqSection />
          <UpdatesSection />
          <FinalCta />
          <PublicFooter />
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
